import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, X, MapPin, Star as StarIcon, Loader2, Flame, TrendingUp } from "lucide-react";
import NumericRating from "@/components/NumericRating";
import RatingConfetti from "@/components/RatingConfetti";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function Discover() {
  const [candidates, setCandidates] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [confettiKey, setConfettiKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feed");
      setCandidates(data);
      setIdx(0);
      setPhotoIdx(0);
    } catch {
      toast.error("Could not load feed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/weekly-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const current = candidates[idx];

  const swipe = async (action) => {
    if (!current || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/swipe", { target_id: current.id, action });
      if (data.matched) {
        toast.success(`It's a match with ${current.display_name}! Check Matches to unlock.`, { duration: 4000 });
      }
      // move to next
      setPhotoIdx(0);
      if (idx + 1 >= candidates.length) {
        await load();
      } else {
        setIdx(idx + 1);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Something went wrong"));
    } finally {
      setSaving(false);
    }
  };

  const ratePhoto = async (rating) => {
    if (!current) return;
    const photo = current.photos[photoIdx];
    if (!photo) return;
    try {
      const { data } = await api.post("/rate", { photo_id: photo.id, rating });
      trackEvent("rate_photo", {
        score: rating,
        is_high: rating >= 8,
        just_golden: !!data?.just_golden,
      });
      const updated = [...candidates];
      updated[idx] = data.owner || data;
      setCandidates(updated);
      if (rating >= 9) setConfettiKey((k) => k + 1);
      if (data.milestone) {
        toast.success(`🏆 ${data.milestone} lifetime ratings — thank you for shaping the community!`, { duration: 5000 });
      }
      if (data.just_golden) {
        toast(`✨ You pushed this photo to Golden (8+)!`, { duration: 4200 });
      }
      if (data.referral_bonus) {
        toast(`🎁 Your friend just got you a free reveal credit`, { duration: 4200 });
      }
      if (data.streak?.credit_awarded) {
        toast.success(`🔥 ${data.streak.streak}-day streak! +1 reveal credit`, { duration: 4200 });
      } else if (data.streak && !data.streak.unchanged) {
        toast.success(`Rating saved · ${data.streak.streak}-day streak`);
      } else {
        toast.success("Rating saved");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not save rating"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  if (!current) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="feed-empty">
        <h2 className="font-serif-display text-4xl md:text-5xl text-[#722f37]">You've seen everyone for now.</h2>
        <p className="mt-4 text-lg text-[#5c5c5c]">Check back soon — new members join every day.</p>
        <div className="mt-8 flex justify-center gap-3">
          <button onClick={load} className="btn-outline" data-testid="feed-refresh-btn">Refresh</button>
          <Link to="/matches" className="btn-primary" data-testid="feed-to-matches">See my matches</Link>
        </div>
      </div>
    );
  }

  const photo = current.photos[photoIdx];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-14">
      {/* Weekly digest strip */}
      {summary && (summary.ratings_given + summary.ratings_received + summary.new_matches > 0 || summary.current_streak > 0) && (
        <div className="mb-8 rounded-2xl bg-white border border-[#e5e0d8] card-elev px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="weekly-digest">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#722f37]" />
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#722f37]">This week</span>
          </div>
          <div className="flex items-baseline gap-1.5"><span className="font-serif-display text-2xl text-[#722f37]">{summary.ratings_given}</span><span className="text-xs text-[#5c5c5c]">rated</span></div>
          <div className="flex items-baseline gap-1.5"><span className="font-serif-display text-2xl text-[#722f37]">{summary.ratings_received}</span><span className="text-xs text-[#5c5c5c]">on you</span></div>
          <div className="flex items-baseline gap-1.5"><span className="font-serif-display text-2xl text-[#722f37]">{summary.new_matches}</span><span className="text-xs text-[#5c5c5c]">new matches</span></div>
          {summary.current_streak > 0 && (
            <div className="flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold bg-[#c39d2b]/15 text-[#8a6a10]" data-testid="digest-streak">
              <Flame className="w-3.5 h-3.5" /> {summary.current_streak}-day streak
            </div>
          )}
          {summary.best_photo_avg != null && (
            <div className="text-xs text-[#5c5c5c]">Your best photo this week: <span className="font-semibold text-[#722f37]">{summary.best_photo_avg}/10</span></div>
          )}
        </div>
      )}

      <div className="flex items-baseline justify-between gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Discover</span>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl mt-2 leading-tight">Who caught your eye today?</h1>
        </div>
        <div className="text-xs sm:text-sm text-[#5c5c5c] shrink-0 whitespace-nowrap" data-testid="feed-progress">
          {idx + 1} / {candidates.length}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10" data-testid={`profile-card-${current.id}`}>
        {/* PHOTO STACK */}
        <div className="md:col-span-3 min-w-0">
          <div className="relative rounded-[1.75rem] overflow-hidden bg-[#eae5d9] card-elev">
            <RatingConfetti fireKey={confettiKey} />
            <img
              src={photo?.data_url}
              alt={current.display_name}
              className="w-full h-[560px] object-cover"
              data-testid="feed-photo"
            />
            {current.photos.length > 1 && false && (
              <div className="absolute top-4 left-4 right-4 flex gap-1">
                {current.photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPhotoIdx(i)}
                    className={`flex-1 h-1 rounded-full transition-colors ${i === photoIdx ? "bg-white" : "bg-white/40"}`}
                    aria-label={`Photo ${i + 1}`}
                    data-testid={`photo-dot-${i}`}
                  />
                ))}
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
              <div className="flex items-end justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-serif-display text-white text-4xl leading-none" data-testid="feed-display-name">
                      {current.display_name}, <span className="opacity-80">{current.age}</span>
                    </h2>
                    {current.verified && <VerifiedBadge size={12} />}
                  </div>
                  {current.location && (
                    <div className="mt-2 flex items-center gap-1 text-white/85 text-sm">
                      <MapPin className="w-4 h-4" /> {current.location}
                    </div>
                  )}
                  <Link
                    to={`/u/${current.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-white/90 underline hover:text-white"
                    data-testid="feed-view-profile"
                  >
                    View full profile →
                  </Link>
                </div>
                <div className="glass rounded-full px-3 py-1.5 flex items-center gap-1 text-sm">
                  <StarIcon className="w-4 h-4 text-[#c39d2b] fill-[#c39d2b]" />
                  <span className="font-medium" data-testid="feed-overall-rating">{current.overall_rating || "—"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* PHOTO RATING */}
          <div className="mt-6 p-6 rounded-2xl bg-white border border-[#e5e0d8] card-elev">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm uppercase tracking-[0.22em] font-bold text-[#722f37]">
                  Rate this photo · 1 to 10
                </div>
                <div className="text-sm text-[#5c5c5c] mt-1">
                  Community average: <span className="font-semibold text-[#1a1a1a]">{photo?.avg_rating || 0}/10</span> ({photo?.rating_count || 0} rating{(photo?.rating_count || 0) === 1 ? "" : "s"}) · 8+ is Golden
                </div>
              </div>
            </div>
            <div className="mt-4">
              <NumericRating
                value={photo?.my_rating || 0}
                onChange={ratePhoto}
                max={10}
                testId="photo-rating"
              />
            </div>
            {current.interests?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-[#e5e0d8]" data-testid="feed-interests-below-photo">
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#722f37] mb-2">
                  Hobbies & Interests
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {current.interests.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-[#faf9f6] border border-[#e5e0d8] text-xs text-[#1a1a1a]"
                      data-testid={`feed-interest-tag-${tag.toLowerCase()}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PROFILE INFO + ACTIONS */}
        <div className="md:col-span-2 min-w-0 flex flex-col">
          <div className="p-8 rounded-2xl bg-[#faf9f6] border border-[#e5e0d8] card-elev">
            <h3 className="font-serif-display text-2xl text-[#722f37]">About</h3>
            <p className="mt-3 text-lg leading-relaxed text-[#1a1a1a]" data-testid="feed-bio">
              {current.bio || <span className="text-[#5c5c5c] italic">No bio yet.</span>}
            </p>
            {current.interests?.length > 0 && (
              <div className="mt-6">
                <div className="text-sm uppercase tracking-[0.22em] font-bold text-[#722f37] mb-3">Hobbies & Interests</div>
                <div className="flex flex-wrap gap-2">
                  {current.interests.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-white border border-[#e5e0d8] text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Explicit Pass / Like buttons (accessibility for 55+) */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <button
              onClick={() => swipe("pass")}
              disabled={saving}
              className="btn-outline flex items-center justify-center gap-2 h-16 text-lg"
              data-testid="feed-pass-btn"
            >
              <X className="w-6 h-6" /> Pass
            </button>
            <button
              onClick={() => swipe("like")}
              disabled={saving}
              className="btn-primary flex items-center justify-center gap-2 h-16 text-lg"
              data-testid="feed-like-btn"
            >
              <Heart className="w-6 h-6" /> Like
            </button>
          </div>
          <p className="text-xs text-[#5c5c5c] mt-3">Mutual likes create a match. You'll unlock names & full photos from your Matches page.</p>
        </div>
      </div>
    </div>
  );
}
